import { supabase } from './supabase';

// Category structure definition (synced with ETL: scripts/etl-products.js)
const CATEGORY_STRUCTURE: Record<string, { slug: string; level: number; children: (string | { name: string; children: string[] })[] }> = {
  // ── Original 5 categories ──
  'sexual-health': {
    slug: 'sexual-health',
    level: 0,
    children: [
      'sildenafil', 'tadalafil', 'vardenafil', 'udenafil', 'avanafil', 'dapoxetine',
      'sildenafil-delayed', 'tadalafil-delayed', 'vardenafil-delayed',
      'avanafil-delayed', 'combo-products', 'other-sexual'
    ]
  },
  'hair-loss': {
    slug: 'hair-loss',
    level: 0,
    children: [
      'finasteride', 'dutasteride', 'minoxidil', 'aldactone',
      'finasteride-minoxidil', 'dutasteride-minoxidil', 'finasteride-aldactone',
      'topical-finasteride', 'women', 'other'
    ]
  },
  'skin-care': {
    slug: 'skin-care',
    level: 0,
    children: [
      'roaccutane', 'doxycycline', 'minocycline', 'retinol',
      'adapalene', 'tretinoin', 'benzoyl-peroxide', 'nadifloxacin', 'azelaic-acid', 'other'
    ]
  },
  'weight-management': {
    slug: 'weight-management',
    level: 0,
    children: [
      'rybelsus', 'fat-blocker', 'carb-blocker', 'contrave',
      'teneligliptin', 'topiramate', 'diuretic', 'supplements', 'other'
    ]
  },
  'beauty-skincare': {
    slug: 'beauty-skincare',
    level: 0,
    children: [
      'melasma-whitening', 'eyelash-growth', 'placenta', 'ivermectin-cream',
      'hydroquinone', 'acyclovir', 'trifarotene'
    ]
  },
  // ── New 11 categories ──
  'heart-health': {
    slug: 'heart-health',
    level: 0,
    children: [
      'ace-inhibitors', 'arb', 'beta-blockers', 'calcium-channel',
      'anticoagulants', 'antiplatelets', 'cholesterol', 'other'
    ]
  },
  'diabetes': {
    slug: 'diabetes',
    level: 0,
    children: [
      'metformin', 'sulfonylureas', 'dpp4-inhibitors', 'sglt2-inhibitors',
      'insulin', 'glp1-agonists', 'other'
    ]
  },
  'eye-care': {
    slug: 'eye-care',
    level: 0,
    children: ['glaucoma', 'dry-eye', 'infection', 'allergy', 'other']
  },
  'pain-relief': {
    slug: 'pain-relief',
    level: 0,
    children: ['nsaids', 'opioid', 'neuropathic', 'muscle-relaxant', 'migraine', 'other']
  },
  'antibiotics': {
    slug: 'antibiotics',
    level: 0,
    children: [
      'penicillin', 'cephalosporin', 'macrolide', 'fluoroquinolone',
      'tetracycline', 'antifungal', 'other'
    ]
  },
  'cancer-oncology': {
    slug: 'cancer-oncology',
    level: 0,
    children: [
      'targeted-therapy', 'hormone-therapy', 'chemotherapy',
      'immunotherapy', 'supportive-care', 'other'
    ]
  },
  'respiratory': {
    slug: 'respiratory',
    level: 0,
    children: ['bronchodilators', 'inhalers', 'antihistamines', 'corticosteroids', 'leukotriene', 'other']
  },
  'stomach-digestive': {
    slug: 'stomach-digestive',
    level: 0,
    children: ['ppi', 'h2-blockers', 'antacids', 'antiemetics', 'laxatives', 'liver-support', 'other']
  },
  'mental-health': {
    slug: 'mental-health',
    level: 0,
    children: ['ssri', 'snri', 'antipsychotics', 'anxiolytics', 'anticonvulsants', 'sleep-aids', 'other']
  },
  'vitamins-supplements': {
    slug: 'vitamins-supplements',
    level: 0,
    children: ['multivitamins', 'calcium-d3', 'iron', 'b-complex', 'omega-3', 'probiotics', 'other']
  },
  'general-medicine': {
    slug: 'general-medicine',
    level: 0,
    children: ['thyroid', 'prostate-urinary', 'bone-joint', 'kidney-renal', 'allergy-immunity', 'antiviral', 'hormones', 'other']
  },
};

// Function to convert name to slug (names are already in English slug format)
const createSlug = (name: string): string => {
  return name.toLowerCase().replace(/[^\w\s-]/g, '').replace(/\s+/g, '-');
};

// New category structure migration
export const migrateCategoriesFromProductCategoryTxt = async () => {
  try {
    console.log('Starting migration based on product_category.txt...');

    // Delete existing data
    await supabase.from('categories').delete().neq('id', 0);
    console.log('Cleared existing categories');

    // Create primary (root) categories
    const rootCategories = [];
    let displayOrder = 1;

    for (const [categoryName, categoryInfo] of Object.entries(CATEGORY_STRUCTURE)) {
      console.log(`🔄 Creating root category: ${categoryName}`);
      const { data, error } = await supabase
        .from('categories')
        .insert({
          name: categoryName,
          level: 0,
          parent_id: null
        })
        .select()
        .single();

      if (error) {
        console.error('❌ Error creating root category:', categoryName, error);
        throw error;
      }

      rootCategories.push({ ...data, children: categoryInfo.children });
      console.log(`✅ Created root category: ${categoryName} (ID: ${data.id})`);
    }

    // Create secondary and tertiary categories
    for (const rootCategory of rootCategories) {
      let childOrder = 1;
      
      for (const child of rootCategory.children) {
        if (typeof child === 'string') {
          // Create secondary category
          console.log(`🔄 Creating 2nd level category: ${child} under ${rootCategory.name}`);
          const { data, error } = await supabase
            .from('categories')
            .insert({
              name: child,
              level: 1,
              parent_id: rootCategory.id
            })
            .select()
            .single();

          if (error) {
            console.error(`❌ Error creating 2nd level category: ${child}`, error);
          } else {
            console.log(`✅ Created 2nd level category: ${child} (ID: ${data.id})`);
          }
        } else if (typeof child === 'object' && child.children) {
          // Create secondary category (with sub-categories)
          const { data: parent2nd, error: parent2ndError } = await supabase
            .from('categories')
            .insert({
              name: child.name,
              level: 1,
              parent_id: rootCategory.id
            })
            .select()
            .single();

          if (parent2ndError) {
            console.error('Error creating 2nd level parent category:', child.name, parent2ndError);
            continue;
          }

          console.log('Created 2nd level parent category:', child.name);

          // Create tertiary categories
          let grandChildOrder = 1;
          for (const grandChild of child.children) {
            const { data, error } = await supabase
              .from('categories')
              .insert({
                name: grandChild,
                level: 2,
                parent_id: parent2nd.id
              })
              .select()
              .single();

            if (error) {
              console.error('Error creating 3rd level category:', grandChild, error);
            } else {
              console.log('Created 3rd level category:', grandChild);
            }
          }
        }
      }
    }

    return { success: true, message: 'Successfully migrated categories based on product_category.txt' };
  } catch (error) {
    console.error('Migration error:', error);
    return { success: false, message: 'Migration failed: ' + (error as Error).message };
  }
};

// Legacy localStorage migration (backup)
export const migrateCategoriesFromLocalStorage = async () => {
  try {
    // Get existing categories from localStorage
    const savedCategories = localStorage.getItem('admin_categories');
    if (!savedCategories) {
      console.log('No categories found in localStorage');
      return { success: false, message: 'No categories to migrate' };
    }

    const categories = JSON.parse(savedCategories);
    console.log('Found categories in localStorage:', categories.length);

    // Delete existing data (development only)
    await supabase.from('categories').delete().neq('id', 0);

    // Sort categories by level and insert parents first
    const sortedCategories = categories.sort((a: any, b: any) => a.level - b.level);

    for (const category of sortedCategories) {
      const { data, error } = await supabase
        .from('categories')
        .insert({
          name: category.name,
          parent_id: category.parentId || null,
          level: category.level
        })
        .select()
        .single();

      if (error) {
        console.error('Error migrating category:', category.name, error);
      } else {
        console.log('Migrated category:', category.name);
      }
    }

    return { success: true, message: `Migrated ${categories.length} categories` };
  } catch (error) {
    console.error('Migration error:', error);
    return { success: false, message: 'Migration failed' };
  }
};

// Migrate products from localStorage to Supabase
export const migrateProductsFromLocalStorage = async () => {
  try {
    // Product data is hardcoded in AdminContext, so
    // just inserting a few sample products temporarily
    const sampleProducts = [
      {
        id: 'sample-tadalafil-1',
        name: 'Tastylia 5mg (Oral Disintegrating)',
        category: 'tadalafil',
        original_price: 89000,
        discount: 27,
        final_price: 65000,
        manufacturer: 'Healing Pharma',
        rating: 4.6,
        review_count: 201,
        image: '/images/products/ED_MEDS/tastylia5mg.png',
        description: 'Tastylia 5mg is an oral disintegrating tadalafil formulation. Can be taken without water for fast-acting effects.',
        stock: 85
      },
      {
        id: 'sample-finasteride-1',
        name: 'Finpecia 1mg',
        category: 'finasteride',
        original_price: 35000,
        discount: 20,
        final_price: 28000,
        manufacturer: 'Cipla',
        rating: 4.4,
        review_count: 156,
        image: '/images/products/HAIR_LOSS/finpecia.jpg',
        description: 'Finpecia is a finasteride-based hair loss treatment.',
        stock: 120
      }
    ];

    // Delete existing data (development only)
    await supabase.from('products').delete().neq('id', '');

    for (const product of sampleProducts) {
      const { data, error } = await supabase
        .from('products')
        .insert(product)
        .select()
        .single();

      if (error) {
        console.error('Error migrating product:', product.name, error);
      } else {
        console.log('Migrated product:', product.name);
      }
    }

    return { success: true, message: `Migrated ${sampleProducts.length} products` };
  } catch (error) {
    console.error('Product migration error:', error);
    return { success: false, message: 'Product migration failed' };
  }
};

// Run full migration (based on product_category.txt)
export const runFullMigration = async () => {
  console.log('Starting full migration based on product_category.txt...');
  
  const categoryResult = await migrateCategoriesFromProductCategoryTxt();
  console.log('Category migration:', categoryResult);
  
  const productResult = await migrateProductsFromLocalStorage();
  console.log('Product migration:', productResult);
  
  return {
    categories: categoryResult,
    products: productResult
  };
};

// Legacy localStorage-based migration (backup)
export const runLegacyMigration = async () => {
  console.log('Starting legacy migration from localStorage...');
  
  const categoryResult = await migrateCategoriesFromLocalStorage();
  console.log('Category migration:', categoryResult);
  
  const productResult = await migrateProductsFromLocalStorage();
  console.log('Product migration:', productResult);
  
  return {
    categories: categoryResult,
    products: productResult
  };
};